import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { useTranslation } from '@lib/useTypedTranslation';

import { DeviceInfoContext } from '../../../../device';
import { DeviceChargeLevel } from '../../../../device/compositions/performance/charts/deviceChargeLevel';
import { filterDeviceEvents, getDeviceEvents } from '../../../../device/compositions/performance/events/deviceEvents';
import { getActiveEventFilters } from '../../../../device/compositions/performance/events/edit-events';
import { getSelectedTzDateDeviceChargeLevel, getSelectedTzDateDeviceEventsList, ITzBatteryLevel } from '../../../../../../services/core/devicePerformance';
import { TzDeviceEvent } from '../../../../../../services/core/eventsTypes';
import { RequestInitWithRetry } from '../../../../../../lib/request';
import { useWorldRequest } from '../../../../../../lib/useWorldRequest';
import { useFeatureTogglesContext } from '../../../../../../context/featureToggles';
import { useWorldSettingsContext } from '../../../../../../context/worldSettings';
import { Loading } from '../../../../../../components/loading/loading';
import { italicAxisTitle } from '../../../../../../components/chart/lib';
import { useAnomalyExaminationEventFilters } from '../useAnomalyExaminationEventFilters';
import { AnomalySectionProps } from './index';

// DeviceChargeLevel's chart-container only gets its designed 14rem height (and the event overlay
// icons/ticks their correctly-scaled positions) from charts.css rules scoped under this ancestor
// class - the same wrapper selectedDateReport.tsx uses in the performance tab. Without it the
// chart collapses to a tiny default height and the event markers render outside its bounds.
import '../../../../device/compositions/performance/reports.css';

// On/off charge and low power markers are enabled by default here, deliberately differing from
// the performance tab's charge level chart (which also defaults batteryChanged on).
const INITIALLY_ENABLED_EVENTS = ['deviceOnPower', 'deviceOffPower', 'deviceLowPower'] as const;
// Unlike the plain performance-tab charge level chart, this page's own low power marker cannot be
// disabled - it's the one event the page exists to examine. Mirrors rebootsSection.tsx/
// dropsSection.tsx locking their own primary event the same way.
const LOCKED_EVENTS = ['deviceLowPower'] as const;

type SectionData = [ITzBatteryLevel[], TzDeviceEvent[]];

export function LowPowerSection(props: AnomalySectionProps) {
  const { deviceId, selectedDate, onChartMount } = props;
  const { t } = useTranslation('translation');
  const { platformType, timeZone } = useContext(DeviceInfoContext);
  const featureToggles = useFeatureTogglesContext();
  const { worldSettings } = useWorldSettingsContext();

  const { eventFilters, dispatchEventFilters } = useAnomalyExaminationEventFilters(
    platformType, INITIALLY_ENABLED_EVENTS, LOCKED_EVENTS
  );

  // World-level anomaly-detection time zone selectedDate was bucketed by (see
  // deviceAnomalyEventChart.ts/fillingInDataForDays.ts) - defaults to 'UTC' to match
  // deviceAnomalyExaminationPage.tsx's own fallback for the top chart.
  const anomalyTz = worldSettings.smartGroupingTimeZone || 'UTC';

  const selectedDateUtc = useMemo(() => {
    const selectedDay = moment.utc(selectedDate).format('YYYY-MM-DD');
    return {
      from: moment.tz(selectedDay, anomalyTz).startOf('day').valueOf(),
      to: moment.tz(selectedDay, anomalyTz).endOf('day').valueOf()
    };
  }, [selectedDate, anomalyTz]);

  const deviceTimeZone = timeZone?.name || 'UTC';

  const chargeLevelTitle = t('DEVICE_CHARGE_LEVEL_FOR_RANGE', {
    from: moment.utc(selectedDateUtc.from).tz(deviceTimeZone).format('Do MMMM HH:mm'),
    to: moment.utc(selectedDateUtc.to).tz(deviceTimeZone).format('Do MMMM HH:mm'),
    timeZone: timeZone?.name || 'UTC',
    ns: 'translation'
  });

  // Same axis-title convention selectedDateChart.tsx (the network tab's signal strength chart)
  // already uses for labelling an axis with the device's own time zone. Italicised to match every
  // other examination chart's own x-axis title (see deviceAnomaliesDataUsage.tsx/
  // eventTimelineSection.tsx).
  const xAxisTitle = italicAxisTitle(t('DEVICE_LOCAL_TIME', { ns: 'translation', timeZone: timeZone?.name ? ` (${timeZone.name})` : '' }));

  const eventNames = useMemo(() => Object.values(getDeviceEvents(platformType, featureToggles)).map(event => event.name), [platformType, featureToggles]);

  const dataFetcher = useCallback(() => {
    const params = { deviceId, to: selectedDateUtc.to, tz: anomalyTz };
    return (options: RequestInitWithRetry) => Promise.all([
      getSelectedTzDateDeviceChargeLevel(params)(options),
      getSelectedTzDateDeviceEventsList({ ...params, events: eventNames })(options)
    ]) as Promise<SectionData>;
  }, [deviceId, selectedDateUtc.to, anomalyTz, eventNames]);

  const {
    loading,
    data: [rawBatteryLevels, rawEvents]
  } = useWorldRequest(dataFetcher, { initialLoading: true, initialData: [[], []] as SectionData });

  // hideChildren mounts DeviceChargeLevel immediately, hidden (display: none) via a wrapping
  // [hidden] attribute, while this section's own data is still loading - so its amCharts instance
  // is first created against a zero-size container.
  // hasBecomeVisible flips false -> true exactly once, the first time loading completes, and never
  // reverts - this is deliberately NOT just `!loading` (which toggles on every date switch), so
  // downstream one-time-only effects (below, and DeviceChargeLevel's own visible-transition
  // recovery) don't re-run (and, in that recovery's case, drop series - see its onDataUpdated) on
  // every subsequent day switch.
  const [hasBecomeVisible, setHasBecomeVisible] = useState(false);
  useEffect(() => {
    if (!loading) {
      setHasBecomeVisible(true);
    }
  }, [loading]);

  // DeviceChargeLevel shares a useAlignCharts group with the page's always-visible top chart (see
  // deviceAnomalyExaminationPage.tsx), which syncs Y-axis widths from whatever each registered
  // chart's own axis measures the FIRST time it reports itself "ready" after registering. If this
  // chart were registered (via onChartMount) immediately at mount - while still hidden above - its
  // axis would report a zero/degenerate measurement right then, permanently locking the shared
  // width down to the top chart's narrower (non-percentage) labels instead of its own "100%"-wide
  // ones - not just at first mount, but every time either chart later re-validates (e.g. on this
  // chart's own subsequent day-switch data reloads), since amCharts refires "ready" - and hence
  // syncAxisWidth - on each one, re-applying whatever the narrower chart currently needs.
  // Deferring registration until hasBecomeVisible (below) avoids ever capturing that bad
  // measurement in the first place, rather than trying to correct it after the fact - onMount
  // itself still fires normally (and exactly once) at genuine component mount, only capturing the
  // chart instance; the actual alignment registration is what's delayed.
  const chartRef = useRef<am4charts.XYChart | null>(null);
  const handleChartMount = useCallback((chart: am4charts.XYChart) => {
    chartRef.current = chart;
    return chart;
  }, []);
  useEffect(() => {
    if (hasBecomeVisible && chartRef.current) {
      onChartMount(chartRef.current);
    }
  }, [hasBecomeVisible, onChartMount]);

  const activeEvents = useMemo(() => getActiveEventFilters(eventFilters.appliedEventFilters), [eventFilters.appliedEventFilters]);
  const [chargeLevelEvents] = useMemo(() => filterDeviceEvents(rawEvents, activeEvents), [rawEvents, activeEvents]);

  return (
    <div className="selectedDateCharts_container device-anomaly-examination-page__battery-chart">
      {/* hideChildren keeps DeviceChargeLevel mounted (just visually hidden) while a new date's
          data loads, instead of unmounting/remounting it. Remounting would dispose its amCharts
          instance and re-add a second, stale entry to the shell's shared useAlignCharts(2) group
          alongside the (never-remounting) top chart, which would throw "EventDispatcher is
          disposed" the next time alignment tries to re-attach a "ready" listener to it. */}
      <Loading isLoading={loading} transparentOverlay={false} hideChildren={true}>
        <DeviceChargeLevel
          classname="selected-date"
          dateRange={selectedDateUtc}
          data={rawBatteryLevels}
          events={chargeLevelEvents}
          displayTimeZone={deviceTimeZone}
          eventFilters={eventFilters}
          dispatchEventFilters={dispatchEventFilters}
          lockedEvents={LOCKED_EVENTS}
          // See hasBecomeVisible above - fires DeviceChargeLevel's one-time hidden-mount axis-width
          // recovery without re-running it (and dropping series) on every later day switch.
          visible={hasBecomeVisible}
          onMount={handleChartMount}
          title={chargeLevelTitle}
          xAxisTitle={xAxisTitle}
          // useAnomalyExaminationEventFilters is a single-chart, session-only reducer that does
          // not handle onApplyAll (applying "all" would be identical to onApply for a single
          // chart) - hide the button rather than offer an action that doesn't actually apply
          // anything "all".
          showApplyAll={false}
        />
      </Loading>
    </div>
  );
}
